/**
 * Client-side Promo Capture Middleware
 *
 * Handles extracting, validating, and persisting promo codes from campaign links.
 * Designed to run on page load with near-zero overhead when no promo is present.
 *
 * Flow:
 * 1. On page load: extract ?promo= from URL, validate, store in cookie/localStorage
 * 2. If authenticated: immediately call capture API
 * 3. On auth completion: transfer stored promo to DB via capture API
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */

import { promoService } from '../../services/promoService';

// --- Constants ---

/** Cookie name for storing the promo code. */
const PROMO_COOKIE_NAME = 'mp_promo';

/** localStorage key for the promo code backup. */
const PROMO_STORAGE_KEY = 'mp_promo';

/** localStorage flag indicating the promo has been successfully captured to DB. */
const PROMO_CAPTURED_FLAG = 'mp_promo_captured';

/** Strict regex for validating promo code format. */
const PROMO_CODE_REGEX = /^[A-Z0-9-]{1,50}$/;

/** ISO 8601 expiry for the BETA-BOGO-2026 promotion. */
const BETA_BOGO_EXPIRY = '2026-06-30T23:59:59Z';

// --- Cookie helpers ---

function setPromoCookie(code: string): void {
  const expires = new Date(BETA_BOGO_EXPIRY).toUTCString();
  document.cookie = `${PROMO_COOKIE_NAME}=${encodeURIComponent(code)}; Secure; SameSite=Lax; Path=/; expires=${expires}`;
}

function getPromoCookie(): string | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${PROMO_COOKIE_NAME}=`));

  if (!match) return null;

  const value = decodeURIComponent(match.split('=')[1]);
  return value || null;
}

function clearPromoCookie(): void {
  document.cookie = `${PROMO_COOKIE_NAME}=; Secure; SameSite=Lax; Path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// --- localStorage helpers ---

function setPromoStorage(code: string): void {
  try {
    localStorage.setItem(PROMO_STORAGE_KEY, code);
  } catch {
    // localStorage unavailable — cookie is primary store
  }
}

function getPromoStorage(): string | null {
  try {
    return localStorage.getItem(PROMO_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearPromoStorage(): void {
  try {
    localStorage.removeItem(PROMO_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

function setPromoCapturedFlag(): void {
  try {
    localStorage.setItem(PROMO_CAPTURED_FLAG, 'true');
  } catch {
    // Ignore
  }
}

function isPromoCaptured(): boolean {
  try {
    return localStorage.getItem(PROMO_CAPTURED_FLAG) === 'true';
  } catch {
    return false;
  }
}

// --- Validation ---

function isValidPromoCode(code: string): boolean {
  return PROMO_CODE_REGEX.test(code);
}

function isPromoExpired(): boolean {
  return Date.now() > new Date(BETA_BOGO_EXPIRY).getTime();
}

// --- Core logic ---

/**
 * Reads the stored promo code from cookie or localStorage.
 * Returns null if no promo is stored or if it's invalid/expired.
 */
export function getStoredPromoCode(): string | null {
  const fromCookie = getPromoCookie();
  const fromStorage = getPromoStorage();
  const code = fromCookie || fromStorage;

  if (!code) return null;
  if (!isValidPromoCode(code)) return null;
  if (isPromoExpired()) return null;

  return code;
}

/**
 * Captures a promo code to the backend for the authenticated user.
 * Sets the captured flag on success to prevent redundant calls.
 */
async function capturePromoToBackend(promoCode: string): Promise<boolean> {
  try {
    await promoService.capturePromo(promoCode);
    setPromoCapturedFlag();
    return true;
  } catch {
    // Capture failed — will retry on next auth completion or page load
    return false;
  }
}

/**
 * Initialize promo capture on page load.
 *
 * Short-circuits immediately if no ?promo= in URL (near-zero overhead).
 * Extracts, validates, and stores the promo code.
 * Strips ?promo= from URL to prevent Referrer header leakage.
 *
 * @param isAuthenticated - Whether the current user is authenticated
 */
export function initPromoCapture(isAuthenticated: boolean): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const promoParam = url.searchParams.get('promo');

  // Short-circuit: no ?promo= in URL — check if we need to capture stored promo
  if (!promoParam) {
    // If authenticated and have a stored promo that hasn't been captured yet, capture it
    if (isAuthenticated && !isPromoCaptured()) {
      const storedCode = getStoredPromoCode();
      if (storedCode) {
        capturePromoToBackend(storedCode);
      }
    }
    return;
  }

  // Normalize to uppercase for validation
  const promoCode = promoParam.toUpperCase();

  // Validate format
  if (!isValidPromoCode(promoCode)) {
    // Invalid code — strip from URL and bail
    stripPromoFromUrl(url);
    return;
  }

  // Check expiry client-side before storing
  if (isPromoExpired()) {
    stripPromoFromUrl(url);
    return;
  }

  // Store in cookie and localStorage
  setPromoCookie(promoCode);
  setPromoStorage(promoCode);

  // Strip ?promo= from URL to prevent Referrer header leakage
  stripPromoFromUrl(url);

  // If authenticated, immediately capture to backend
  if (isAuthenticated) {
    capturePromoToBackend(promoCode);
  }
}

/**
 * Strips the ?promo= parameter from the current URL using history.replaceState.
 * Prevents Referrer header leakage of the promo code.
 */
function stripPromoFromUrl(url: URL): void {
  url.searchParams.delete('promo');
  const cleanUrl = url.pathname + (url.search || '') + url.hash;
  window.history.replaceState({}, '', cleanUrl);
}

/**
 * Called on auth completion (sign-in/sign-up success).
 * Reads promo from cookie/localStorage and calls the capture endpoint.
 *
 * This function should be called from auth success handlers.
 *
 * @param userId - The authenticated user's ID (used for logging/tracing; the backend
 *   identifies the user from the session cookie via authenticatedFetch)
 */
export async function transferPromoOnAuth(userId: string): Promise<void> {
  if (typeof window === 'undefined') return;

  // Skip if already captured
  if (isPromoCaptured()) return;

  const promoCode = getStoredPromoCode();
  if (!promoCode) return;

  await capturePromoToBackend(promoCode);
}

/**
 * Clears all promo-related browser storage.
 * Useful for testing or when promo is redeemed.
 */
export function clearPromoData(): void {
  clearPromoCookie();
  clearPromoStorage();
  try {
    localStorage.removeItem(PROMO_CAPTURED_FLAG);
  } catch {
    // Ignore
  }
}
